<template>
  <div class="dashboard">

    <!-- Welcome Header -->
    <div class="dash-header">
      <div>
        <h5 class="dash-welcome">Welcome back, <span>{{ user?.first_name }}</span> 👋</h5>
        <p class="dash-referal">Referral ID: <strong>{{ user?.my_referal_code }}</strong></p>
      </div>
      <div class="dash-wallet-id" v-if="wallet && (wallet.account_number || wallet.wallet_no)">
        <span class="wallet-label">Account No.</span>
        <span class="wallet-value">{{ wallet.account_number || wallet.wallet_no }}</span>
        <span class="wallet-label" style="margin-top:2px">{{ wallet.bank_name || '' }}</span>
      </div>
    </div>

    <!-- ── Module Tabs ─────────────────────────────────────────────────── -->
    <div class="tab-bar-wrap">
      <div class="tab-bar">
        <button class="tab-btn" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">
          <font-awesome-icon icon="th-large" class="tab-icon" />
          Overview
        </button>
        <button v-if="(!planCan || planCan.sales_access) && (!permission || permission.view_sales)"
          class="tab-btn" :class="{ active: activeTab === 'sales' }" @click="activeTab = 'sales'">
          <font-awesome-icon icon="chart-line" class="tab-icon" />
          Sales
        </button>
        <button v-if="(!planCan || planCan.purchase_access) && (!permission || permission.view_purchase)"
          class="tab-btn" :class="{ active: activeTab === 'purchases' }" @click="activeTab = 'purchases'">
          <font-awesome-icon icon="shopping-basket" class="tab-icon" />
          Purchases
        </button>
        <button v-if="!permission || permission.view_product"
          class="tab-btn" :class="{ active: activeTab === 'inventory' }" @click="activeTab = 'inventory'">
          <font-awesome-icon icon="boxes" class="tab-icon" />
          Inventory
        </button>
        <button v-if="!permission || permission.view_expense || permission.view_invoice || permission.view_debtors || permission.view_creditors"
          class="tab-btn" :class="{ active: activeTab === 'finance' }" @click="activeTab = 'finance'">
          <font-awesome-icon icon="file-invoice-dollar" class="tab-icon" />
          Finance
        </button>
        <button v-if="(!planCan || planCan.hr_access || planCan.payroll_access) && (!permission || permission.view_employee || permission.view_payroll)"
          class="tab-btn" :class="{ active: activeTab === 'hr' }" @click="activeTab = 'hr'">
          <font-awesome-icon icon="users" class="tab-icon" />
          HR & Payroll
        </button>
        <button v-if="(!planCan || planCan.procurement_access) && (!permission || permission.view_vendors || permission.manage_vendors || permission.view_purchase_orders || permission.view_purchase_requisitions || permission.view_sourcing_events)"
          class="tab-btn" :class="{ active: activeTab === 'procurement' }" @click="activeTab = 'procurement'">
          <font-awesome-icon icon="clipboard-list" class="tab-icon" />
          Procurement
        </button>
        <button v-if="(!planCan || planCan.crm_access) && (!permission || permission.crm_module)"
          class="tab-btn" :class="{ active: activeTab === 'crm' }" @click="activeTab = 'crm'">
          <font-awesome-icon icon="handshake" class="tab-icon" />
          CRM
        </button>
        <button v-if="(!planCan || planCan.visitors_access) && (!permission || permission.manage_visitors || permission.view_visitor_reports)"
          class="tab-btn" :class="{ active: activeTab === 'visitors' }" @click="activeTab = 'visitors'">
          <font-awesome-icon icon="id-badge" class="tab-icon" />
          Visitors
        </button>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: OVERVIEW
    ══════════════════════════════════════════════════════════════════ -->
    <template v-if="activeTab === 'overview'">

      <!-- KPI Cards Row 1 -->
      <div class="stat-grid">
        <a class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Wallet Balance</span>
            <span class="stat-value">{{ $filters.formatMoney(wallet_balance, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--green"><font-awesome-icon icon="wallet" /></div>
        </a>

        <router-link v-if="(!planCan || planCan.sales_access) && (!permission || permission.view_sales)"
          :to="$url('/all-sales?todaysdate=' + todayDate)"
          class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Revenue Today</span>
            <span class="stat-value">{{ $filters.formatMoney(revenue, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--yellow"><font-awesome-icon icon="chart-line" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.debitor_access) && (!permission || permission.view_debtors)" :to="$url('/debtor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Debtors</span>
            <span class="stat-value">{{ $filters.formatMoney(debtors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="user-minus" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.creditor_access) && (!permission || permission.view_creditors)" :to="$url('/creditor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Creditors</span>
            <span class="stat-value">{{ $filters.formatMoney(creditors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="user-plus" /></div>
        </router-link>
      </div>

      <!-- KPI Cards Row 2: cross-module operational summary -->
      <div class="stat-grid">
        <router-link v-if="!permission || permission.view_product" :to="$url('/all-products')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Products</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_products }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="boxes" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.expense_access) && (!permission || permission.view_expense)" :to="$url('/manage-expenses')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Expenses</span>
            <span class="stat-value" style="color:#1a2b45">{{ $filters.formatMoney(total_expenses, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--orange"><font-awesome-icon icon="money-bill-wave" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.administrator_access) && (!permission || permission.view_administration)" :to="$url('/manage-user')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Users</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_users }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="users" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.administrator_access) && (!permission || permission.view_administration)" :to="$url('/manage-store')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Stores</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_stores }}</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="store" /></div>
        </router-link>
      </div>

      <!-- Quick Actions + Module Summary -->
      <div class="row g-3 mt-0">
        <div class="col-md-5">
          <div class="side-panel h-100">
            <p class="side-panel-title">Quick Actions</p>
            <div class="quick-actions">
              <router-link v-if="(!planCan || planCan.sales_access) && (!permission || permission.view_sales)" :to="$url('/make-sales')" class="qa-btn">
                <div class="qa-icon qa-icon--blue"><font-awesome-icon icon="cash-register" /></div>
                <span>New Sale</span>
              </router-link>
              <router-link v-if="(!planCan || planCan.purchase_access) && (!permission || permission.view_purchase)" :to="$url('/make-purchase')" class="qa-btn">
                <div class="qa-icon qa-icon--purple"><font-awesome-icon icon="shopping-basket" /></div>
                <span>Purchase</span>
              </router-link>
              <router-link v-if="(!planCan || planCan.invoice_access) && (!permission || permission.view_invoice)" :to="$url('/create-invoice')" class="qa-btn">
                <div class="qa-icon qa-icon--teal"><font-awesome-icon icon="file-invoice" /></div>
                <span>Invoice</span>
              </router-link>
              <router-link v-if="(!planCan || planCan.expense_access) && (!permission || permission.view_expense)" :to="$url('/create-expense')" class="qa-btn">
                <div class="qa-icon qa-icon--orange"><font-awesome-icon icon="money-bill-wave" /></div>
                <span>Expense</span>
              </router-link>
              <router-link v-if="!permission || permission.view_product" :to="$url('/create-product')" class="qa-btn">
                <div class="qa-icon qa-icon--green"><font-awesome-icon icon="plus-circle" /></div>
                <span>Product</span>
              </router-link>
              <router-link v-if="(!planCan || planCan.administrator_access) && (!permission || permission.view_administration)" :to="$url('/manage-user')" class="qa-btn">
                <div class="qa-icon qa-icon--red"><font-awesome-icon icon="user-plus" /></div>
                <span>Add User</span>
              </router-link>
            </div>
          </div>
        </div>
        <div class="col-md-7">
          <div class="side-panel h-100">
            <p class="side-panel-title">Business Summary</p>
            <div v-if="(!planCan || planCan.sales_access) && (!permission || permission.view_sales)" class="alert-row">
              <div class="alert-dot" style="background:#ca8a04"></div>
              <div class="alert-text">
                <span class="alert-label">Revenue Today</span>
                <span class="alert-count" style="color:#ca8a04;font-size:13px;">{{ $filters.formatMoney(revenue, company?.currency?.symbol) }}</span>
              </div>
              <router-link :to="$url('/all-sales?todaysdate=' + todayDate)" class="alert-link">View</router-link>
            </div>
            <div v-if="(!planCan || planCan.expense_access) && (!permission || permission.view_expense)" class="alert-row">
              <div class="alert-dot alert-dot--orange"></div>
              <div class="alert-text">
                <span class="alert-label">Total Expenses</span>
                <span class="alert-count alert-count--orange" style="font-size:13px;">{{ $filters.formatMoney(total_expenses, company?.currency?.symbol) }}</span>
              </div>
              <router-link :to="$url('/manage-expenses')" class="alert-link">View</router-link>
            </div>
            <div v-if="(!planCan || planCan.debitor_access) && (!permission || permission.view_debtors)" class="alert-row">
              <div class="alert-dot alert-dot--red"></div>
              <div class="alert-text">
                <span class="alert-label">Debtors</span>
                <span class="alert-count alert-count--red" style="font-size:13px;">{{ $filters.formatMoney(debtors, company?.currency?.symbol) }}</span>
              </div>
              <router-link :to="$url('/debtor')" class="alert-link">View</router-link>
            </div>
            <div v-if="(!planCan || planCan.creditor_access) && (!permission || permission.view_creditors)" class="alert-row">
              <div class="alert-dot alert-dot--purple"></div>
              <div class="alert-text">
                <span class="alert-label">Creditors</span>
                <span class="alert-count" style="color:#7c3aed;font-size:13px;">{{ $filters.formatMoney(creditors, company?.currency?.symbol) }}</span>
              </div>
              <router-link :to="$url('/creditor')" class="alert-link">View</router-link>
            </div>
            <div v-if="!permission || permission.view_product" class="alert-row">
              <div class="alert-dot alert-dot--blue"></div>
              <div class="alert-text">
                <span class="alert-label">Total Products</span>
                <span class="alert-count alert-count--blue">{{ total_products }}</span>
              </div>
              <router-link :to="$url('/all-products')" class="alert-link">View</router-link>
            </div>
            <div v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_employee)" class="alert-row">
              <div class="alert-dot alert-dot--green"></div>
              <div class="alert-text">
                <span class="alert-label">Total Employees</span>
                <span class="alert-count alert-count--green">{{ total_users }}</span>
              </div>
              <router-link :to="$url('/manage-employees')" class="alert-link">View</router-link>
            </div>
            <div v-if="(!planCan || planCan.administrator_access) && (!permission || permission.view_administration)" class="alert-row">
              <div class="alert-dot" style="background:#6366f1"></div>
              <div class="alert-text">
                <span class="alert-label">Total Users</span>
                <span class="alert-count" style="color:#6366f1">{{ total_users }}</span>
              </div>
              <router-link :to="$url('/manage-user')" class="alert-link">View</router-link>
            </div>
            <div class="alert-row">
              <div class="alert-dot alert-dot--teal"></div>
              <div class="alert-text">
                <span class="alert-label">Total Stores</span>
                <span class="alert-count alert-count--teal">{{ total_stores }}</span>
              </div>
              <router-link :to="$url('/manage-store')" class="alert-link">View</router-link>
            </div>
            <div class="alert-row">
              <div class="alert-dot" style="background:#0891b2"></div>
              <div class="alert-text">
                <span class="alert-label">Total Referrals</span>
                <span class="alert-count" style="color:#0891b2">{{ total_referral }}</span>
              </div>
            </div>
            <div v-if="(!planCan || planCan.visitors_access) && (!permission || permission.manage_visitors || permission.view_visitor_reports)" class="alert-row">
              <div class="alert-dot" style="background:#0ea5e9"></div>
              <div class="alert-text">
                <span class="alert-label">Active Visitors</span>
                <span class="alert-count" style="color:#0ea5e9">{{ visitorKpis.active_now ?? '—' }}</span>
              </div>
              <router-link :to="$url('/hr/visitors/active')" class="alert-link">View</router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- ── All Modules Grid ── -->
      <div class="modules-section">
        <p class="modules-section-title">All Modules</p>
        <div class="modules-grid">

          <router-link v-if="(!planCan || planCan.sales_access) && (!permission || permission.view_sales)" :to="$url('/all-sales')" class="mod-card">
            <div class="mod-icon" style="background:rgba(202,138,4,0.12);color:#ca8a04;">
              <font-awesome-icon icon="cash-register" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Sales</span>
              <span class="mod-desc">Manage transactions &amp; revenue</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.purchase_access) && (!permission || permission.view_purchase)" :to="$url('/all-purchases')" class="mod-card">
            <div class="mod-icon" style="background:rgba(124,58,237,0.12);color:#7c3aed;">
              <font-awesome-icon icon="shopping-basket" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Purchases</span>
              <span class="mod-desc">Track stock purchases &amp; suppliers</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="!permission || permission.view_product" :to="$url('/all-products')" class="mod-card">
            <div class="mod-icon" style="background:rgba(13,148,136,0.12);color:#0d9488;">
              <font-awesome-icon icon="boxes" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Inventory</span>
              <span class="mod-desc">Products, stock levels &amp; transfers</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.invoice_access) && (!permission || permission.view_invoice)" :to="$url('/invoice-history')" class="mod-card">
            <div class="mod-icon" style="background:rgba(0,74,173,0.12);color:#004AAD;">
              <font-awesome-icon icon="file-invoice" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Invoices</span>
              <span class="mod-desc">Create &amp; manage customer invoices</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.billing_access) && (!permission || permission.view_billing)" :to="$url('/manage-billing')" class="mod-card">
            <div class="mod-icon" style="background:rgba(0,74,173,0.12);color:#004AAD;">
              <font-awesome-icon icon="receipt" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Billing</span>
              <span class="mod-desc">Recurring billing &amp; subscriptions</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.expense_access) && (!permission || permission.view_expense)" :to="$url('/manage-expenses')" class="mod-card">
            <div class="mod-icon" style="background:rgba(224,140,0,0.12);color:#e08c00;">
              <font-awesome-icon icon="money-bill-wave" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Expenses</span>
              <span class="mod-desc">Record &amp; categorise business expenses</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.debitor_access) && (!permission || permission.view_debtors)" :to="$url('/debtor')" class="mod-card">
            <div class="mod-icon" style="background:rgba(224,53,53,0.12);color:#e03535;">
              <font-awesome-icon icon="user-minus" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Debtors</span>
              <span class="mod-desc">Outstanding receivables from customers</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.creditor_access) && (!permission || permission.view_creditors)" :to="$url('/creditor')" class="mod-card">
            <div class="mod-icon" style="background:rgba(124,58,237,0.12);color:#7c3aed;">
              <font-awesome-icon icon="user-plus" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Creditors</span>
              <span class="mod-desc">Manage amounts owed to suppliers</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link :to="$url('/accounting/dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(5,150,105,0.12);color:#059669;">
              <font-awesome-icon icon="calculator" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Accounting</span>
              <span class="mod-desc">Ledgers, journals &amp; financial periods</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_employee)" :to="$url('/employees-dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(22,163,74,0.12);color:#16a34a;">
              <font-awesome-icon icon="users" />
            </div>
            <div class="mod-info">
              <span class="mod-name">HR</span>
              <span class="mod-desc">Leave, attendance &amp; workforce</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.payroll_access) && (!permission || permission.view_payroll)" :to="$url('/payroll/dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(6,182,212,0.12);color:#0891b2;">
              <font-awesome-icon icon="file-invoice-dollar" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Payroll</span>
              <span class="mod-desc">Process salaries &amp; payslips</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.procurement_access) && (!permission || permission.view_vendors || permission.manage_vendors || permission.view_purchase_orders || permission.view_purchase_requisitions || permission.view_sourcing_events)" :to="$url('/procurement/dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(8,145,178,0.12);color:#0891b2;">
              <font-awesome-icon icon="clipboard-list" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Procurement</span>
              <span class="mod-desc">Vendors, purchase orders &amp; sourcing</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.crm_access) && (!permission || permission.crm_module)" :to="$url('/crm/dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(0,74,173,0.12);color:#004AAD;">
              <font-awesome-icon icon="handshake" />
            </div>
            <div class="mod-info">
              <span class="mod-name">CRM</span>
              <span class="mod-desc">Leads, opportunities &amp; customer engagement</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.asset_access) && (!permission || permission.view_assets)" :to="$url('/assets')" class="mod-card">
            <div class="mod-icon" style="background:rgba(109,40,217,0.12);color:#6d28d9;">
              <font-awesome-icon icon="tools" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Assets</span>
              <span class="mod-desc">Track &amp; manage company assets</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link :to="$url('/hr/chat')" class="mod-card">
            <div class="mod-icon" style="background:rgba(16,185,129,0.12);color:#10b981;">
              <font-awesome-icon icon="comments" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Chat</span>
              <span class="mod-desc">Internal messaging &amp; documents</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="!permission || permission.view_reports" :to="$url('/all-reports')" class="mod-card">
            <div class="mod-icon" style="background:rgba(245,158,11,0.12);color:#d97706;">
              <font-awesome-icon icon="chart-bar" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Reports</span>
              <span class="mod-desc">Analytics, exports &amp; summaries</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.administrator_access) && (!permission || permission.view_administration)" :to="$url('/manage-user')" class="mod-card">
            <div class="mod-icon" style="background:rgba(99,102,241,0.12);color:#6366f1;">
              <font-awesome-icon icon="user-cog" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Administration</span>
              <span class="mod-desc">Users, roles &amp; store management</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link :to="$url('/company-info')" class="mod-card">
            <div class="mod-icon" style="background:rgba(100,116,139,0.12);color:#64748b;">
              <font-awesome-icon icon="cog" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Settings</span>
              <span class="mod-desc">Company profile &amp; preferences</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.visitors_access) && (!permission || permission.manage_visitors || permission.view_visitor_reports)" :to="$url('/hr/visitors/dashboard')" class="mod-card">
            <div class="mod-icon" style="background:rgba(14,165,233,0.12);color:#0ea5e9;">
              <font-awesome-icon icon="id-badge" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Visitors</span>
              <span class="mod-desc">Check-in, badges &amp; visitor logs</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

          <router-link v-if="(!planCan || planCan.developer_access) && (!permission || permission.manage_api_keys)" :to="$url('/developer/api-keys')" class="mod-card">
            <div class="mod-icon" style="background:rgba(99,102,241,0.12);color:#6366f1;">
              <font-awesome-icon icon="code" />
            </div>
            <div class="mod-info">
              <span class="mod-name">Developer</span>
              <span class="mod-desc">API keys, webhooks &amp; integrations</span>
            </div>
            <font-awesome-icon icon="chevron-right" class="mod-arrow" />
          </router-link>

        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: SALES
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'sales'">

      <!-- Summary cards -->
      <div class="stat-grid stat-grid--3">
        <router-link v-if="!permission || permission.view_sales" :to="$url('/all-sales?todaysdate=' + todayDate)" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Revenue Today</span>
            <span class="stat-value">{{ $filters.formatMoney(revenue, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--yellow"><font-awesome-icon icon="chart-line" /></div>
        </router-link>
        <router-link v-if="(!planCan || planCan.debitor_access) && (!permission || permission.view_debtors)" :to="$url('/debtor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Debtors</span>
            <span class="stat-value">{{ $filters.formatMoney(debtors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="user-minus" /></div>
        </router-link>
        <router-link v-if="!permission || permission.view_product" :to="$url('/all-products')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Products</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_products }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="shopping-cart" /></div>
        </router-link>
      </div>

      <!-- Sales Performance Chart -->
      <div v-if="sales_matrix?.length > 1" class="chart-card">
        <h6 class="chart-title">Sales Performance</h6>
        <GChart type="ColumnChart" :data="sales_matrix" :options="sales_matrix_options" />
      </div>

      <!-- Sales Per Location + Top Customers -->
      <div class="row g-3 mt-0">
        <div v-if="sales_perstore_chart?.length > 0" class="col-md-7">
          <div class="chart-card h-100">
            <h6 class="chart-title">Sales Performance Per Location</h6>
            <GChart type="LineChart" :data="sales_perstore_chart" :options="sales_perstore_options" />
          </div>
        </div>
        <div v-if="top_customer_chart?.length > 1" class="col-md-5">
          <div class="chart-card h-100">
            <h6 class="chart-title">Top Buying Customers</h6>
            <GChart type="AreaChart" :data="top_customer_chart" :options="top_customer_options" />
          </div>
        </div>
      </div>

      <!-- Top Selling Products Chart -->
      <div v-if="top_selling_chart?.length > 1" class="chart-card">
        <h6 class="chart-title">Top Selling Products</h6>
        <GChart type="ComboChart" :data="top_selling_chart" :options="top_selling_options" />
      </div>

      <!-- Top 10 Table -->
      <div class="table-card">
        <div class="table-card-header">
          <h6 class="table-card-title">Top 10 Selling Products</h6>
          <select class="period-select" @change="topProducts()" v-model="period">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="lastweek">Last Week</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div v-if="loading" class="table-empty">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        </div>
        <div v-else-if="!topproducts?.length" class="table-empty">
          <img src="../private-assets/images/emptypng.png" alt="empty" style="max-height:120px;" />
          <p class="mt-3 fw-semibold text-muted">No sales data yet</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Cost Price ({{ company?.currency?.symbol }})</th>
                <th>Selling Price ({{ company?.currency?.symbol }})</th>
                <th>Quantity</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in topproducts" :key="product.id">
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <img v-if="product.image" :src="host.hostName + product.image" class="prod-img" />
                    <span class="prod-name">{{ product.product_name }}</span>
                  </div>
                </td>
                <td>{{ $filters.formatMoney(product.cost_price) }}</td>
                <td>{{ $filters.formatMoney(product.selling_price) }}</td>
                <td>
                  <span class="qty-badge" :class="product.quantity < 5 ? 'qty-low' : ''">{{ product.quantity }}</span>
                </td>
                <td class="text-muted">{{ formatDate(product.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: PURCHASES
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'purchases'">

      <div class="stat-grid stat-grid--3">
        <router-link v-if="(!planCan || planCan.creditor_access) && (!permission || permission.view_creditors)" :to="$url('/creditor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Creditors</span>
            <span class="stat-value">{{ $filters.formatMoney(creditors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="user-plus" /></div>
        </router-link>
        <router-link v-if="!permission || permission.view_product" :to="$url('/all-products')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Products</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_products }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="boxes" /></div>
        </router-link>
        <router-link :to="$url('/manage-store')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Stores</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_stores }}</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="store" /></div>
        </router-link>
      </div>

      <!-- Top Suppliers Chart -->
      <div v-if="top_supplier_chart?.length > 1" class="chart-card">
        <h6 class="chart-title">Top Suppliers</h6>
        <GChart type="AreaChart" :data="top_supplier_chart" :options="top_supplier_options" />
      </div>

      <div v-if="!(top_supplier_chart?.length > 1)" class="empty-tab">
        <font-awesome-icon icon="shopping-basket" class="empty-tab-icon" />
        <p class="empty-tab-title">No Purchase Data Yet</p>
        <p class="empty-tab-sub">Purchase transactions will appear here once recorded.</p>
        <router-link :to="$url('/make-purchase')" class="btn-tab-action">Record a Purchase</router-link>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: INVENTORY
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'inventory'">

      <!-- Inventory KPIs -->
      <div class="stat-grid">
        <router-link v-if="!permission || permission.view_product" :to="$url('/all-products')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Products</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_products }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="boxes" /></div>
        </router-link>

        <router-link :to="$url('/manage-store')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Stores</span>
            <span class="stat-value" style="color:#1a2b45">{{ total_stores }}</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="store" /></div>
        </router-link>

        <router-link v-if="!permission || permission.view_product_location" :to="$url('/all-product-location?outofstock')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Out of Stock</span>
            <span class="stat-value stat-value--danger">{{ out_of_stock }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="times-circle" /></div>
        </router-link>

        <router-link v-if="!permission || permission.view_product_location" :to="$url('/all-product-location?totallowstock')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Low Stock</span>
            <span class="stat-value stat-value--warning">{{ low_stock }}</span>
          </div>
          <div class="stat-icon stat-icon--orange"><font-awesome-icon icon="exclamation-triangle" /></div>
        </router-link>
      </div>

      <!-- Expired + Stock health -->
      <div class="stat-grid stat-grid--2">
        <router-link v-if="!permission || permission.view_expired_products" :to="$url('/expired-products?period=0')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Expired Products</span>
            <span class="stat-value stat-value--danger">{{ expired_products }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="calendar-times" /></div>
        </router-link>
        <a class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Stock Health</span>
            <span class="stat-value" :class="out_of_stock > 0 ? 'stat-value--danger' : 'text-success'">
              {{ out_of_stock === 0 && low_stock === 0 ? 'All Good' : out_of_stock + ' Critical' }}
            </span>
          </div>
          <div class="stat-icon" :class="out_of_stock > 0 ? 'stat-icon--red' : 'stat-icon--green'">
            <font-awesome-icon :icon="out_of_stock > 0 ? 'exclamation-circle' : 'check-circle'" />
          </div>
        </a>
      </div>

      <!-- Stock Alerts panel -->
      <div v-if="!permission || permission.view_product_location" class="side-panel">
        <p class="side-panel-title">Stock Alerts</p>
        <div class="row g-0">
          <div class="col-md-4">
            <div class="alert-row">
              <div class="alert-dot alert-dot--red"></div>
              <div class="alert-text">
                <span class="alert-label">Out of Stock</span>
                <span class="alert-count alert-count--red">{{ out_of_stock }}</span>
              </div>
              <router-link :to="$url('/all-product-location?outofstock')" class="alert-link">View</router-link>
            </div>
          </div>
          <div class="col-md-4">
            <div class="alert-row">
              <div class="alert-dot alert-dot--orange"></div>
              <div class="alert-text">
                <span class="alert-label">Low Stock</span>
                <span class="alert-count alert-count--orange">{{ low_stock }}</span>
              </div>
              <router-link :to="$url('/all-product-location?totallowstock')" class="alert-link">View</router-link>
            </div>
          </div>
          <div class="col-md-4">
            <div class="alert-row">
              <div class="alert-dot alert-dot--red"></div>
              <div class="alert-text">
                <span class="alert-label">Expired</span>
                <span class="alert-count alert-count--red">{{ expired_products }}</span>
              </div>
              <router-link :to="$url('/expired-products?period=0')" class="alert-link">View</router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Inventory breakdown panel -->
      <div class="module-detail-card">
        <div class="module-detail-hd">
          <font-awesome-icon icon="boxes" class="mdh-icon" style="color:#004AAD;" />
          <div>
            <p class="mdh-title">Inventory Breakdown</p>
            <p class="mdh-sub">Stock health across all stores</p>
          </div>
        </div>
        <div class="module-stats-list">
          <div class="msl-row">
            <span class="msl-label">Total Products</span>
            <span class="msl-bar-wrap">
              <span class="msl-bar msl-bar--blue" :style="{ width: '100%' }"></span>
            </span>
            <span class="msl-val msl-val--blue">{{ total_products }}</span>
          </div>
          <div class="msl-row">
            <span class="msl-label">In Stock</span>
            <span class="msl-bar-wrap">
              <span class="msl-bar msl-bar--green"
                :style="{ width: total_products > 0 ? ((total_products - out_of_stock - low_stock) / total_products * 100) + '%' : '0%' }">
              </span>
            </span>
            <span class="msl-val msl-val--green">{{ Math.max(0, total_products - out_of_stock - low_stock) }}</span>
          </div>
          <div class="msl-row">
            <span class="msl-label">Low Stock</span>
            <span class="msl-bar-wrap">
              <span class="msl-bar msl-bar--orange"
                :style="{ width: total_products > 0 ? (low_stock / total_products * 100) + '%' : '0%' }">
              </span>
            </span>
            <span class="msl-val msl-val--orange">{{ low_stock }}</span>
          </div>
          <div class="msl-row">
            <span class="msl-label">Out of Stock</span>
            <span class="msl-bar-wrap">
              <span class="msl-bar msl-bar--red"
                :style="{ width: total_products > 0 ? (out_of_stock / total_products * 100) + '%' : '0%' }">
              </span>
            </span>
            <span class="msl-val msl-val--red">{{ out_of_stock }}</span>
          </div>
          <div class="msl-row">
            <span class="msl-label">Expired</span>
            <span class="msl-bar-wrap">
              <span class="msl-bar msl-bar--red"
                :style="{ width: total_products > 0 ? (expired_products / total_products * 100) + '%' : '0%' }">
              </span>
            </span>
            <span class="msl-val msl-val--red">{{ expired_products }}</span>
          </div>
        </div>
        <div class="module-detail-actions">
          <router-link :to="$url('/all-products')" class="mda-link">All Products</router-link>
          <router-link v-if="!permission || permission.view_product" :to="$url('/create-product')" class="mda-link mda-link--primary">Add Product</router-link>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: FINANCE
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'finance'">

      <!-- Finance KPI row -->
      <div class="stat-grid">
        <a class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Wallet Balance</span>
            <span class="stat-value">{{ $filters.formatMoney(wallet_balance, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--green"><font-awesome-icon icon="wallet" /></div>
        </a>

        <router-link v-if="(!planCan || planCan.expense_access) && (!permission || permission.view_expense)" :to="$url('/manage-expenses')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Expenses</span>
            <span class="stat-value">{{ $filters.formatMoney(total_expenses, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--orange"><font-awesome-icon icon="money-bill-wave" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.debitor_access) && (!permission || permission.view_debtors)" :to="$url('/debtor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Debtors</span>
            <span class="stat-value">{{ $filters.formatMoney(debtors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="user-minus" /></div>
        </router-link>

        <router-link v-if="(!planCan || planCan.creditor_access) && (!permission || permission.view_creditors)" :to="$url('/creditor')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Creditors</span>
            <span class="stat-value">{{ $filters.formatMoney(creditors, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="user-plus" /></div>
        </router-link>
      </div>

      <!-- Invoice cards -->
      <div v-if="(!planCan || planCan.invoice_access || planCan.billing_access) && (!permission || permission.view_invoice)" class="stat-grid stat-grid--3">
        <router-link v-if="!planCan || planCan.invoice_access" :to="$url('/invoice-history')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Total Invoices</span>
            <span class="stat-value" style="color:#1a2b45">{{ $filters.formatMoney(total_invoices, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="file-invoice" /></div>
        </router-link>

        <router-link v-if="!planCan || planCan.invoice_access" :to="$url('/invoice-payment-history')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Paid Invoices</span>
            <span class="stat-value stat-value--success">{{ $filters.formatMoney(total_paid_invoices, company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--green"><font-awesome-icon icon="check-circle" /></div>
        </router-link>

        <router-link v-if="!planCan || planCan.invoice_access" :to="$url('/invoice-history')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Outstanding</span>
            <span class="stat-value stat-value--danger">{{ $filters.formatMoney(Math.max(0, total_invoices - total_paid_invoices), company?.currency?.symbol) }}</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="hourglass-half" /></div>
        </router-link>
      </div>

      <!-- Upgrade prompt for plans without invoice/billing/expense access -->
      <div v-if="planCan && !planCan.invoice_access && !planCan.expense_access && !planCan.billing_access" class="upgrade-banner">
        <div class="upgrade-banner-icon"><font-awesome-icon icon="lock" /></div>
        <div class="upgrade-banner-text">
          <strong>Unlock Invoices, Billing &amp; Expenses</strong>
          <span>Upgrade to the Business plan or higher to access invoicing, recurring billing, and expense tracking.</span>
        </div>
        <router-link :to="$url('/subscribe')" class="upgrade-banner-btn">Upgrade Plan</router-link>
      </div>

      <!-- Finance breakdown panel -->
      <div class="row g-3">
        <div v-if="(!planCan || planCan.debitor_access || planCan.creditor_access) && (!permission || permission.view_debtors || permission.view_creditors)" class="col-md-6">
          <div class="module-detail-card">
            <div class="module-detail-hd">
              <font-awesome-icon icon="file-invoice-dollar" class="mdh-icon" style="color:#7c3aed;" />
              <div>
                <p class="mdh-title">Receivables vs Payables</p>
                <p class="mdh-sub">Debtors owed to you vs creditors you owe</p>
              </div>
            </div>
            <div class="module-stats-list">
              <div class="msl-row">
                <span class="msl-label">Debtors (Receivable)</span>
                <span class="msl-bar-wrap">
                  <span class="msl-bar msl-bar--blue"
                    :style="{ width: (debtors + creditors) > 0 ? (debtors / (debtors + creditors) * 100) + '%' : '50%' }">
                  </span>
                </span>
                <span class="msl-val msl-val--blue">{{ $filters.formatMoney(debtors, company?.currency?.symbol) }}</span>
              </div>
              <div class="msl-row">
                <span class="msl-label">Creditors (Payable)</span>
                <span class="msl-bar-wrap">
                  <span class="msl-bar msl-bar--red"
                    :style="{ width: (debtors + creditors) > 0 ? (creditors / (debtors + creditors) * 100) + '%' : '50%' }">
                  </span>
                </span>
                <span class="msl-val msl-val--red">{{ $filters.formatMoney(creditors, company?.currency?.symbol) }}</span>
              </div>
            </div>
            <div class="module-detail-actions">
              <router-link v-if="!planCan || planCan.debitor_access" :to="$url('/debtor')" class="mda-link">Debtors</router-link>
              <router-link v-if="!planCan || planCan.creditor_access" :to="$url('/creditor')" class="mda-link">Creditors</router-link>
            </div>
          </div>
        </div>
        <div v-if="(!planCan || planCan.invoice_access || planCan.expense_access) && (!permission || permission.view_invoice || permission.view_expense)" class="col-md-6">
          <div class="module-detail-card">
            <div class="module-detail-hd">
              <font-awesome-icon icon="money-bill-wave" class="mdh-icon" style="color:#e08c00;" />
              <div>
                <p class="mdh-title">Invoice &amp; Expense Summary</p>
                <p class="mdh-sub">Paid vs outstanding invoices &amp; expenses</p>
              </div>
            </div>
            <div class="module-stats-list">
              <div class="msl-row">
                <span class="msl-label">Paid</span>
                <span class="msl-bar-wrap">
                  <span class="msl-bar msl-bar--green"
                    :style="{ width: total_invoices > 0 ? (total_paid_invoices / total_invoices * 100) + '%' : '0%' }">
                  </span>
                </span>
                <span class="msl-val msl-val--green">{{ $filters.formatMoney(total_paid_invoices, company?.currency?.symbol) }}</span>
              </div>
              <div class="msl-row">
                <span class="msl-label">Outstanding</span>
                <span class="msl-bar-wrap">
                  <span class="msl-bar msl-bar--red"
                    :style="{ width: total_invoices > 0 ? (Math.max(0, total_invoices - total_paid_invoices) / total_invoices * 100) + '%' : '0%' }">
                  </span>
                </span>
                <span class="msl-val msl-val--red">{{ $filters.formatMoney(Math.max(0, total_invoices - total_paid_invoices), company?.currency?.symbol) }}</span>
              </div>
              <div v-if="!planCan || planCan.expense_access" class="msl-row">
                <span class="msl-label">Total Expenses</span>
                <span class="msl-bar-wrap">
                  <span class="msl-bar msl-bar--orange" style="width:60%"></span>
                </span>
                <span class="msl-val msl-val--orange">{{ $filters.formatMoney(total_expenses, company?.currency?.symbol) }}</span>
              </div>
            </div>
            <div class="module-detail-actions">
              <router-link v-if="!planCan || planCan.expense_access" :to="$url('/manage-expenses')" class="mda-link">Expenses</router-link>
              <router-link v-if="!planCan || planCan.invoice_access" :to="$url('/create-invoice')" class="mda-link mda-link--primary">New Invoice</router-link>
            </div>
          </div>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: HR & PAYROLL
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'hr'">

      <!-- HR KPIs -->
      <div class="stat-grid stat-grid--3">
        <router-link v-if="!permission || permission.view_employee" :to="$url('/manage-employees')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Employees</span>
            <span class="stat-value">{{ total_users }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="users" /></div>
        </router-link>
        <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_leave)" :to="$url('/hr/leave')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Leave Management</span>
            <span class="stat-value" style="font-size:14px;">Active</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="umbrella-beach" /></div>
        </router-link>
        <router-link v-if="(!planCan || planCan.payroll_access) && (!permission || permission.view_payroll)" :to="$url('/payroll/dashboard')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Payroll</span>
            <span class="stat-value" style="color:#1a2b45;font-size:14px;">Dashboard</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="file-invoice-dollar" /></div>
        </router-link>
      </div>

      <!-- Payroll Charts -->
      <template v-if="(!planCan || planCan.payroll_access) && (!permission || permission.view_payroll) && trends.length">
        <div class="chart-card">
          <h6 class="chart-title">Payroll Trends</h6>
          <PayrollLineChart :trends="trends" />
        </div>
        <div class="row g-3 mt-0">
          <div class="col-md-5">
            <div class="chart-card">
              <h6 class="chart-title">Payroll Breakdown</h6>
              <PayrollPieChart :breakdown="breakdown" />
            </div>
          </div>
          <div class="col-md-7">
            <div class="chart-card">
              <h6 class="chart-title">Payroll Growth Insight</h6>
              <PayrollInsightsChart :insights="insights" />
              <p class="text-center text-muted mt-2" style="font-size:13px;">{{ insights.message }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- Quick HR links -->
      <div class="module-detail-card">
        <div class="module-detail-hd">
          <font-awesome-icon icon="users" class="mdh-icon" style="color:#059669;" />
          <div>
            <p class="mdh-title">HR Quick Links</p>
            <p class="mdh-sub">Navigate to key HR management areas</p>
          </div>
        </div>
        <div class="quick-actions" style="margin-top:14px;">
          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_employee)" :to="$url('/manage-employees')" class="qa-btn">
            <div class="qa-icon qa-icon--blue"><font-awesome-icon icon="user-tie" /></div>
            <span>Employees</span>
          </router-link>
          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_leave)" :to="$url('/hr/leave')" class="qa-btn">
            <div class="qa-icon qa-icon--teal"><font-awesome-icon icon="umbrella-beach" /></div>
            <span>Leave</span>
          </router-link>
          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_attendance)" :to="$url('/hr/attendance')" class="qa-btn">
            <div class="qa-icon qa-icon--green"><font-awesome-icon icon="clock" /></div>
            <span>Attendance</span>
          </router-link>
          <router-link v-if="(!planCan || planCan.payroll_access) && (!permission || permission.view_payroll)" :to="$url('/payroll/dashboard')" class="qa-btn">
            <div class="qa-icon qa-icon--purple"><font-awesome-icon icon="money-bill-wave" /></div>
            <span>Payroll</span>
          </router-link>
          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_loans)" :to="$url('/hr/loans')" class="qa-btn">
            <div class="qa-icon qa-icon--orange"><font-awesome-icon icon="hand-holding-usd" /></div>
            <span>Loans</span>
          </router-link>
          <router-link v-if="(!planCan || planCan.hr_access) && (!permission || permission.view_employee)" :to="$url('/hr/recruitment')" class="qa-btn">
            <div class="qa-icon qa-icon--red"><font-awesome-icon icon="briefcase" /></div>
            <span>Recruitment</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: PROCUREMENT
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'procurement'">

      <div class="stat-grid stat-grid--3">
        <router-link v-if="!permission || permission.view_vendors || permission.manage_vendors" :to="$url('/procurement/vendors')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Vendor Directory</span>
            <span class="stat-value" style="font-size:14px;">View All</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="building" /></div>
        </router-link>
        <router-link v-if="!permission || permission.view_purchase_orders" :to="$url('/procurement/purchase-orders')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Purchase Orders</span>
            <span class="stat-value" style="font-size:14px;">View All</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="file-invoice" /></div>
        </router-link>
        <router-link v-if="!permission || permission.view_purchase_requisitions" :to="$url('/procurement/purchase-requisitions')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Purchase Requisitions</span>
            <span class="stat-value" style="color:#1a2b45;font-size:14px;">View All</span>
          </div>
          <div class="stat-icon stat-icon--orange"><font-awesome-icon icon="clipboard-list" /></div>
        </router-link>
      </div>

      <div class="module-detail-card">
        <div class="module-detail-hd">
          <font-awesome-icon icon="clipboard-list" class="mdh-icon" style="color:#0891b2;" />
          <div>
            <p class="mdh-title">Procurement Quick Links</p>
            <p class="mdh-sub">Navigate to key procurement areas</p>
          </div>
        </div>
        <div class="quick-actions" style="margin-top:14px;">
          <router-link :to="$url('/procurement/dashboard')" class="qa-btn">
            <div class="qa-icon qa-icon--teal"><font-awesome-icon icon="gauge" /></div>
            <span>Dashboard</span>
          </router-link>
          <router-link v-if="!permission || permission.view_vendors || permission.manage_vendors" :to="$url('/procurement/vendors')" class="qa-btn">
            <div class="qa-icon qa-icon--blue"><font-awesome-icon icon="building" /></div>
            <span>Vendors</span>
          </router-link>
          <router-link v-if="!permission || permission.view_purchase_requisitions" :to="$url('/procurement/purchase-requisitions')" class="qa-btn">
            <div class="qa-icon qa-icon--orange"><font-awesome-icon icon="clipboard-list" /></div>
            <span>Requisitions</span>
          </router-link>
          <router-link v-if="!permission || permission.view_purchase_orders" :to="$url('/procurement/purchase-orders')" class="qa-btn">
            <div class="qa-icon qa-icon--purple"><font-awesome-icon icon="file-invoice" /></div>
            <span>Purchase Orders</span>
          </router-link>
          <router-link v-if="!permission || permission.view_sourcing_events" :to="$url('/procurement/sourcing-events')" class="qa-btn">
            <div class="qa-icon qa-icon--green"><font-awesome-icon icon="handshake" /></div>
            <span>Sourcing Events</span>
          </router-link>
          <router-link v-if="!permission || permission.view_projects || permission.manage_projects" :to="$url('/procurement/projects')" class="qa-btn">
            <div class="qa-icon qa-icon--red"><font-awesome-icon icon="project-diagram" /></div>
            <span>Projects</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: CRM
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'crm'">

      <!-- CRM KPIs -->
      <div class="stat-grid">
        <router-link :to="$url('/crm/leads')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Total Leads</span>
            <span class="stat-value">{{ crmKpis.total_leads ?? 0 }}</span>
            <span v-if="crmKpis.new_leads_this_month" style="font-size:11px;color:rgba(255,255,255,.5);margin-top:2px;">{{ crmKpis.new_leads_this_month }} this month</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="user-plus" /></div>
        </router-link>
        <router-link :to="$url('/crm/opportunities')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Open Opportunities</span>
            <span class="stat-value">{{ crmKpis.open_opportunities ?? 0 }}</span>
            <span v-if="crmKpis.pipeline_value" style="font-size:11px;color:rgba(255,255,255,.5);margin-top:2px;">{{ fmtMoney(crmKpis.pipeline_value) }} pipeline</span>
          </div>
          <div class="stat-icon stat-icon--yellow"><font-awesome-icon icon="chart-line" /></div>
        </router-link>
        <router-link :to="$url('/crm/cases')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Open Cases</span>
            <span class="stat-value">{{ crmKpis.open_cases ?? 0 }}</span>
            <span v-if="crmKpis.sla_breached" style="font-size:11px;color:#fca5a5;margin-top:2px;">{{ crmKpis.sla_breached }} SLA breached</span>
          </div>
          <div class="stat-icon stat-icon--red"><font-awesome-icon icon="ticket-alt" /></div>
        </router-link>
        <router-link :to="$url('/crm/activities')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">Pending Activities</span>
            <span class="stat-value" style="color:#1a2b45">{{ crmKpis.pending_activities ?? 0 }}</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="calendar-check" /></div>
        </router-link>
      </div>

      <!-- CRM skeleton while loading -->
      <div v-if="crmLoading" class="crm-skeleton-row">
        <div class="crm-skeleton-chart"></div>
        <div class="crm-skeleton-chart"></div>
      </div>

      <!-- Charts row 1: Leads by Status + Pipeline -->
      <div v-else class="row g-3 mt-0">
        <div class="col-md-5">
          <div class="chart-card h-100">
            <h6 class="chart-title">Leads by Status</h6>
            <div v-if="!crmLeadsStatusChart.length" class="crm-chart-empty">No lead data</div>
            <GChart v-else type="PieChart" :data="crmLeadsStatusChart" :options="crmDonutOpts" />
          </div>
        </div>
        <div class="col-md-7">
          <div class="chart-card h-100">
            <h6 class="chart-title">Opportunity Pipeline by Stage</h6>
            <div v-if="!crmPipelineChart.length" class="crm-chart-empty">No pipeline data</div>
            <GChart v-else type="ColumnChart" :data="crmPipelineChart" :options="crmColumnOpts" />
          </div>
        </div>
      </div>

      <!-- Charts row 2: Lead trend + Won vs Lost -->
      <div v-if="!crmLoading" class="row g-3 mt-0">
        <div class="col-md-7">
          <div class="chart-card h-100">
            <h6 class="chart-title">Lead Volume — Last 6 Months</h6>
            <div v-if="!crmLeadsTrendChart.length" class="crm-chart-empty">No trend data</div>
            <GChart v-else type="LineChart" :data="crmLeadsTrendChart" :options="crmLineOpts" />
          </div>
        </div>
        <div class="col-md-5">
          <div class="chart-card h-100">
            <h6 class="chart-title">Activities by Type</h6>
            <div v-if="!crmActivitiesChart.length" class="crm-chart-empty">No activity data</div>
            <GChart v-else type="BarChart" :data="crmActivitiesChart" :options="crmBarOpts" />
          </div>
        </div>
      </div>

      <!-- CRM Quick links -->
      <div class="module-detail-card">
        <div class="module-detail-hd">
          <font-awesome-icon icon="handshake" class="mdh-icon" style="color:#004AAD;" />
          <div>
            <p class="mdh-title">CRM Quick Links</p>
            <p class="mdh-sub">Navigate to key CRM areas</p>
          </div>
        </div>
        <div class="quick-actions" style="margin-top:14px;">
          <router-link :to="$url('/crm/dashboard')" class="qa-btn">
            <div class="qa-icon qa-icon--blue"><font-awesome-icon icon="gauge" /></div>
            <span>Dashboard</span>
          </router-link>
          <router-link :to="$url('/crm/leads')" class="qa-btn">
            <div class="qa-icon qa-icon--green"><font-awesome-icon icon="user-plus" /></div>
            <span>Leads</span>
          </router-link>
          <router-link :to="$url('/crm/contacts')" class="qa-btn">
            <div class="qa-icon qa-icon--teal"><font-awesome-icon icon="address-book" /></div>
            <span>Contacts</span>
          </router-link>
          <router-link :to="$url('/crm/opportunities')" class="qa-btn">
            <div class="qa-icon qa-icon--yellow"><font-awesome-icon icon="chart-line" /></div>
            <span>Opportunities</span>
          </router-link>
          <router-link :to="$url('/crm/cases')" class="qa-btn">
            <div class="qa-icon qa-icon--red"><font-awesome-icon icon="ticket-alt" /></div>
            <span>Cases</span>
          </router-link>
          <router-link :to="$url('/crm/campaigns')" class="qa-btn">
            <div class="qa-icon qa-icon--orange"><font-awesome-icon icon="bullhorn" /></div>
            <span>Campaigns</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ══════════════════════════════════════════════════════════════════
         TAB: VISITORS
    ══════════════════════════════════════════════════════════════════ -->
    <template v-else-if="activeTab === 'visitors'">

      <!-- KPI Cards -->
      <div class="stat-grid">
        <router-link :to="$url('/hr/visitors/active')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Active Now</span>
            <span class="stat-value">{{ visitorKpis.active_now ?? 0 }}</span>
          </div>
          <div class="stat-icon stat-icon--green"><font-awesome-icon icon="user-check" /></div>
        </router-link>
        <router-link :to="$url('/hr/visitors/history')" class="stat-card stat-card--dark">
          <div class="stat-info">
            <span class="stat-label">Today's Visitors</span>
            <span class="stat-value">{{ visitorKpis.today_total ?? 0 }}</span>
          </div>
          <div class="stat-icon stat-icon--blue"><font-awesome-icon icon="id-badge" /></div>
        </router-link>
        <router-link :to="$url('/hr/visitors/history')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">This Week</span>
            <span class="stat-value" style="color:#1a2b45">{{ visitorKpis.week_total ?? 0 }}</span>
          </div>
          <div class="stat-icon stat-icon--teal"><font-awesome-icon icon="calendar-week" /></div>
        </router-link>
        <router-link :to="$url('/hr/visitors/history')" class="stat-card stat-card--light">
          <div class="stat-info">
            <span class="stat-label" style="color:#1a2b45">This Month</span>
            <span class="stat-value" style="color:#1a2b45">{{ visitorKpis.month_total ?? 0 }}</span>
          </div>
          <div class="stat-icon stat-icon--purple"><font-awesome-icon icon="calendar-alt" /></div>
        </router-link>
      </div>

      <!-- Loading skeleton -->
      <div v-if="visitorsLoading" class="crm-skeleton-row">
        <div class="crm-skeleton-chart"></div>
        <div class="crm-skeleton-chart"></div>
      </div>

      <template v-else>
        <!-- Recent Activity + Top Hosts -->
        <div class="row g-3 mt-0">
          <!-- Recent Activity -->
          <div class="col-md-8">
            <div class="table-card">
              <div class="table-card-header">
                <h6 class="table-card-title">Recent Activity</h6>
                <router-link :to="$url('/hr/visitors/history')" class="alert-link">View all</router-link>
              </div>
              <div v-if="!visitorRecent.length" class="table-empty">
                <p class="text-muted">No visitor activity yet</p>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Visitor</th>
                      <th>Purpose</th>
                      <th>Host</th>
                      <th>Status</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="v in visitorRecent" :key="v.id">
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="emp-avatar-initials" style="width:30px;height:30px;font-size:11px;flex-shrink:0;">{{ v.visitor_initials }}</div>
                          <div>
                            <div style="font-size:13px;font-weight:600;color:#1a2b45;">{{ v.visitor_name }}</div>
                            <div v-if="v.visitor_company" style="font-size:11px;color:#94a3b8;">{{ v.visitor_company }}</div>
                          </div>
                        </div>
                      </td>
                      <td style="font-size:13px;color:#5a6a85;">{{ v.purpose || '—' }}</td>
                      <td style="font-size:13px;color:#5a6a85;">{{ v.host_name }}</td>
                      <td>
                        <span class="status-badge" :class="v.status === 'checked_in' ? 'status-badge--active' : v.status === 'checked_out' ? 'status-badge--inactive' : 'status-badge--pending'">
                          {{ v.status === 'checked_in' ? 'In' : v.status === 'checked_out' ? 'Out' : v.status }}
                        </span>
                      </td>
                      <td style="font-size:12px;color:#94a3b8;">{{ formatDate(v.checked_in_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Top Hosts + Avg Duration -->
          <div class="col-md-4">
            <div class="module-detail-card h-100" style="margin-bottom:0;">
              <div class="module-detail-hd">
                <font-awesome-icon icon="user-tie" class="mdh-icon" style="color:#0ea5e9;" />
                <div>
                  <p class="mdh-title">Top Hosts This Month</p>
                  <p class="mdh-sub">Employees with most visitors</p>
                </div>
              </div>
              <div v-if="!visitorTopHosts.length" style="padding:16px 0;text-align:center;color:#94a3b8;font-size:13px;">No host data yet</div>
              <div v-else class="module-stats-list">
                <div v-for="(h, i) in visitorTopHosts" :key="i" class="msl-row">
                  <span class="msl-label" style="display:flex;align-items:center;gap:8px;">
                    <span class="emp-avatar-initials" style="width:26px;height:26px;font-size:10px;flex-shrink:0;">{{ h.initials }}</span>
                    {{ h.name }}
                  </span>
                  <span class="msl-bar-wrap">
                    <span class="msl-bar msl-bar--blue"
                      :style="{ width: visitorTopHosts[0]?.visit_count > 0 ? (h.visit_count / visitorTopHosts[0].visit_count * 100) + '%' : '0%' }">
                    </span>
                  </span>
                  <span class="msl-val msl-val--blue">{{ h.visit_count }}</span>
                </div>
              </div>
              <div v-if="visitorKpis.avg_duration" style="margin-top:14px;padding-top:12px;border-top:1px solid #f0f4fa;font-size:12px;color:#94a3b8;text-align:center;">
                Avg visit duration this week: <strong style="color:#0ea5e9;">{{ fmtDuration(visitorKpis.avg_duration) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Quick Links -->
      <div class="module-detail-card">
        <div class="module-detail-hd">
          <font-awesome-icon icon="id-badge" class="mdh-icon" style="color:#0ea5e9;" />
          <div>
            <p class="mdh-title">Visitor Quick Links</p>
            <p class="mdh-sub">Navigate to key visitor management areas</p>
          </div>
        </div>
        <div class="quick-actions" style="margin-top:14px;">
          <router-link :to="$url('/hr/visitors/dashboard')" class="qa-btn">
            <div class="qa-icon qa-icon--blue"><font-awesome-icon icon="gauge" /></div>
            <span>Dashboard</span>
          </router-link>
          <router-link v-if="!permission || permission.manage_visitors" :to="$url('/hr/visitors/active')" class="qa-btn">
            <div class="qa-icon qa-icon--green"><font-awesome-icon icon="user-check" /></div>
            <span>Active</span>
          </router-link>
          <router-link v-if="!permission || permission.manage_visitors" :to="$url('/hr/visitors/check-in')" class="qa-btn">
            <div class="qa-icon qa-icon--teal"><font-awesome-icon icon="sign-in-alt" /></div>
            <span>Check-In</span>
          </router-link>
          <router-link v-if="!permission || permission.manage_visitors" :to="$url('/hr/visitors/pre-register')" class="qa-btn">
            <div class="qa-icon qa-icon--purple"><font-awesome-icon icon="calendar-plus" /></div>
            <span>Pre-Register</span>
          </router-link>
          <router-link v-if="!permission || permission.view_visitor_reports" :to="$url('/hr/visitors/history')" class="qa-btn">
            <div class="qa-icon qa-icon--orange"><font-awesome-icon icon="history" /></div>
            <span>History</span>
          </router-link>
          <router-link v-if="!permission || permission.manage_visitor_settings" :to="$url('/hr/visitors/settings')" class="qa-btn">
            <div class="qa-icon qa-icon--red"><font-awesome-icon icon="cog" /></div>
            <span>Settings</span>
          </router-link>
        </div>
      </div>

    </template>

  </div>
</template>

<script>
import axios from "axios";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import host from "../../main";
import { reusableMixin } from '../../lib/reusableMixin';
import moment from 'moment-timezone';
import { GChart } from 'vue-google-charts';
import PayrollLineChart from "./hr/payroll/component/charts/PayrollLineChart.vue";
import PayrollPieChart from "./hr/payroll/component/charts/PayrollPieChart.vue";
import PayrollInsightsChart from "./hr/payroll/component/charts/PayrollInsightsChart.vue";

export default {
  mixins: [reusableMixin],
  components: { GChart, PayrollLineChart, PayrollPieChart, PayrollInsightsChart },

  watch: {
    activeTab(val) {
      if (val === 'crm' && !this.crmLoaded) {
        this.loadCrm();
      }
      if (val === 'visitors' && !this.visitorsLoaded) {
        this.loadVisitors();
      }
    },

    planCan(features) {
      if (!features) return;
      const tabAllowed = {
        sales:       features.sales_access,
        purchases:   features.purchase_access,
        procurement: features.procurement_access,
        hr:          features.hr_access || features.payroll_access,
        crm:         features.crm_access,
      };
      if (this.activeTab in tabAllowed && !tabAllowed[this.activeTab]) {
        this.activeTab = 'overview';
      }
    },

    permission(p) {
      if (p !== null && !p?.view_dashboard) {
        this.$router.replace(this.$url('/hr/my-dashboard'));
        return;
      }
      if (p) {
        const tabAllowed = {
          sales:       p.view_sales,
          purchases:   p.view_purchase,
          inventory:   p.view_product,
          finance:     p.view_expense || p.view_invoice || p.view_debtors || p.view_creditors,
          hr:          p.view_employee || p.view_payroll,
          procurement: p.view_vendors || p.manage_vendors || p.view_purchase_orders || p.view_purchase_requisitions || p.view_sourcing_events,
          crm:         p.crm_module,
        };
        if (this.activeTab in tabAllowed && !tabAllowed[this.activeTab]) {
          this.activeTab = 'overview';
        }
      }
    },
  },

  mounted() {
    const p = this.store.state.getCurrentUserPermission;
    if (p !== null && !p?.view_dashboard) {
      this.$router.replace(this.$url('/hr/my-dashboard'));
      return;
    }
    if (this.planCan) {
      const blocked = {
        sales:       !this.planCan.sales_access,
        purchases:   !this.planCan.purchase_access,
        procurement: !this.planCan.procurement_access,
        hr:          !this.planCan.hr_access && !this.planCan.payroll_access,
        crm:         !this.planCan.crm_access,
      };
      if (blocked[this.activeTab]) this.activeTab = 'overview';
    }
    if (p) {
      const permTabAllowed = {
        sales:       p.view_sales,
        purchases:   p.view_purchase,
        inventory:   p.view_product,
        finance:     p.view_expense || p.view_invoice || p.view_debtors || p.view_creditors,
        hr:          p.view_employee || p.view_payroll,
        procurement: p.view_vendors || p.manage_vendors || p.view_purchase_orders || p.view_purchase_requisitions || p.view_sourcing_events,
        crm:         p.crm_module,
      };
      if (this.activeTab in permTabAllowed && !permTabAllowed[this.activeTab]) {
        this.activeTab = 'overview';
      }
    }

    if (!p || p.view_sales || p.view_purchase) {
      this.fetchChart();
    }
    this.getCompany();
    this.sumInfo();
    this.countInfo();
    if ((!this.planCan || this.planCan.sales_access) && (!p || p.view_sales)) {
      this.topProducts();
    }
    if ((!this.planCan || this.planCan.payroll_access) && (!p || p.view_payroll)) {
      this.load();
    }
    if (this.activeTab === 'visitors') {
      this.loadVisitors();
    }
  },

  methods: {
    async sumInfo() {
      this.period = 'monthly';
      try {
        const res = await axios.get("/dashboard/suminfo");
        this.revenue   = res.data.data?.total_revenue_today;
        this.debtors   = res.data.data.total_debtors ?? 0;
        this.creditors = res.data.data.total_creditor;
      } catch { this.toast.error("Something went wrong"); }
    },

    async load() {
      this.loading = true;
      try {
        const [t, b, i] = await Promise.all([
          axios.get('/dashboard/payroll/trends', { params: this.filters }),
          axios.get('/dashboard/payroll/breakdown', { params: this.filters }),
          axios.get('/dashboard/payroll/insights'),
        ]);
        this.trends    = t.data;
        this.breakdown = b.data;
        this.insights  = i.data;
      } finally { this.loading = false; }
    },

    async countInfo() {
      try {
        const res = await axios.get("/dashboard/countinfo");
        const d = res.data.data;
        this.expired_products    = d.expired_products;
        this.total_products      = d.total_products;
        this.out_of_stock        = d.out_of_stock;
        this.low_stock           = d.low_stock;
        this.total_users         = d.total_users;
        this.total_stores        = d.total_stores;
        this.wallet              = d.wallet || {};
        this.wallet_balance      = d.wallet?.balance ?? 0;
        this.total_referral      = d.total_referral;
        this.total_expenses      = d.total_expenses;
        this.total_invoices      = d.total_invoices;
        this.total_paid_invoices = d.total_paid_invoices;
      } catch (e) { this.toast.error(e.response.data.message); }
    },

    async topProducts() {
      this.loading = true;
      try {
        const res = await axios.get(`/dashboard/topproducts?period=${this.period}`);
        this.topproducts = res.data.data;
      } catch (e) { this.toast.error(e.response.data.message); }
      finally { this.loading = false; }
    },

    async fetchChart() {
      try {
        const res = await axios.get("/dashboard/chart");
        const d = res.data.data;
        this.sales_matrix         = d.sales_matrix.map(Object.values);
        this.sales_perstore_chart = d.sales_per_store.map(Object.values);
        this.top_customer_chart   = d.top_customers.map(Object.values);
        this.top_supplier_chart   = d.top_suppliers.map(Object.values);
        this.top_selling_chart    = d.top_selling_products.map(Object.values);
      } catch (e) { this.toast.error(e.response.data.message); }
    },

    formatDate(date) { return moment(date).format('MMM DD, YYYY'); },

    fmtMoney(v) { return Number(v).toLocaleString('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }); },
    crmCap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; },

    async loadCrm() {
      this.crmLoading = true;
      try {
        const res = await axios.get('/crm/dashboard');
        this.crmRaw   = res.data.data ?? res.data;
        this.crmLoaded = true;
      } catch { /* silently fail — user may not have CRM access */ }
      finally { this.crmLoading = false; }
    },

    async loadVisitors() {
      this.visitorsLoading = true;
      try {
        const res = await axios.get('/visitors/dashboard');
        const d = res.data.data ?? res.data;
        this.visitorKpis     = d.kpi ?? {};
        this.visitorRecent   = d.recent_activity ?? [];
        this.visitorTopHosts = d.top_hosts ?? [];
        this.visitorsLoaded  = true;
      } catch { /* silently fail — user may not have visitor access */ }
      finally { this.visitorsLoading = false; }
    },

    fmtDuration(mins) {
      if (!mins) return '—';
      const h = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    },
  },

  data() {
    return {
      activeTab: 'overview',

      revenue: 0, debtors: 0, creditors: 0,
      low_stock: 0, total_expenses: 0,
      total_invoices: 0, total_paid_invoices: 0,
      wallet: {}, wallet_balance: 0, total_referral: 0,
      total_products: 0, expired_products: 0,
      topproducts: [], period: '',
      out_of_stock: 0, host,
      loading: false, total_users: 0, total_stores: 0,
      toast: useToast(), store: useStore(),
      filters: {}, trends: '', breakdown: '', insights: '',

      sales_matrix: [],
      sales_matrix_options: { title: 'Sales Performance', height: 400 },

      sales_perstore_chart: [],
      sales_perstore_options: { title: 'Sales Per Location', curveType: 'function', legend: { position: 'bottom' }, height: 320 },

      top_customer_chart: [],
      top_customer_options: { title: 'Top Buying Customers', hAxis: { title: 'Customers' }, vAxis: { minValue: 0 }, chartArea: { width: '50%', height: '70%' }, height: 360 },

      top_supplier_chart: [],
      top_supplier_options: { title: 'Top Suppliers', hAxis: { title: 'Suppliers' }, vAxis: { minValue: 0 }, chartArea: { width: '50%', height: '70%' }, height: 360 },

      top_selling_chart: [],
      top_selling_options: { title: 'Top Selling Products', vAxis: { title: 'Sales' }, hAxis: { title: 'Products' }, seriesType: 'bars', series: { 5: { type: 'line' } }, height: 420 },

      // CRM tab
      crmRaw: {},
      crmLoading: false,
      crmLoaded: false,

      // Visitors tab
      visitorKpis: {},
      visitorRecent: [],
      visitorTopHosts: [],
      visitorsLoading: false,
      visitorsLoaded: false,

      crmDonutOpts:  { pieHole: 0.42, height: 300, chartArea: { width: '90%', height: '80%' }, legend: { position: 'right', textStyle: { color: '#5a6a85', fontSize: 12 } }, colors: ['#004AAD','#0ea5e9','#8b5cf6','#f59e0b','#10b981','#ef4444','#ec4899'], tooltip: {} },
      crmColumnOpts: { height: 300, chartArea: { width: '80%', height: '68%' }, legend: { position: 'none' }, hAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: 'transparent' } }, vAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: '#f0f4fa' }, minValue: 0 }, bar: { groupWidth: '55%' }, colors: ['#004AAD'] },
      crmLineOpts:   { height: 300, chartArea: { width: '85%', height: '70%' }, legend: { position: 'none' }, colors: ['#004AAD'], curveType: 'function', pointSize: 5, hAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: 'transparent' } }, vAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: '#f0f4fa' }, minValue: 0 } },
      crmBarOpts:    { height: 300, chartArea: { width: '65%', height: '80%' }, legend: { position: 'none' }, hAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: '#f0f4fa' }, minValue: 0 }, vAxis: { textStyle: { color: '#5a6a85', fontSize: 11 }, gridlines: { color: 'transparent' } }, colors: ['#0ea5e9'] },
    };
  },

  computed: {
    user() { return this.store.state.getLoggedInUser; },
    todayDate() {
      return new Date().toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('/').reverse().join('-');
    },
    planCan() { return this.store.state.planFeatures ?? null; },

    crmKpis()            { return this.crmRaw.kpis               ?? {}; },
    crmLeadsStatusChart() {
      const rows = this.crmRaw.leads_by_status ?? [];
      if (!rows.length) return [];
      return [['Status', 'Count'], ...rows.map(r => [this.crmCap(r.status), Number(r.count)])];
    },
    crmPipelineChart() {
      const rows = this.crmRaw.pipeline_by_stage ?? [];
      if (!rows.length) return [];
      return [['Stage', 'Deals', { role: 'style' }], ...rows.map((r, i) => [this.crmCap(r.stage.replace(/_/g, ' ')), Number(r.count), ['#004AAD','#0ea5e9','#8b5cf6','#f59e0b','#10b981','#ef4444'][i % 6]])];
    },
    crmLeadsTrendChart() {
      const rows = this.crmRaw.leads_trend ?? [];
      if (!rows.length) return [];
      return [['Month', 'New Leads'], ...rows.map(r => [r.month, Number(r.count)])];
    },
    crmActivitiesChart() {
      const rows = this.crmRaw.activities_by_type ?? [];
      if (!rows.length) return [];
      return [['Type', 'Count', { role: 'style' }], ...rows.map((r, i) => [this.crmCap(r.type), Number(r.count), ['#004AAD','#0ea5e9','#8b5cf6','#f59e0b','#10b981','#ef4444'][i % 6]])];
    },
  },
};
</script>

<style scoped>

/* ── Page shell ── */
.dashboard {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Welcome header ── */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 12px;
  padding: 16px 20px;
}
.dash-welcome { margin: 0 0 4px; font-size: 18px; font-weight: 600; color: #1a2b45; }
.dash-welcome span { color: #004AAD; }
.dash-referal { margin: 0; font-size: 13px; color: #7a90b0; }
.dash-referal strong { color: #004AAD; }
.dash-wallet-id { display: flex; flex-direction: column; align-items: flex-end; }
.wallet-label { font-size: 11px; color: #7a90b0; text-transform: uppercase; letter-spacing: 0.05em; }
.wallet-value { font-size: 14px; font-weight: 700; color: #004AAD; }

/* ── Tab bar ── */
.tab-bar-wrap {
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 12px;
  padding: 6px;
  overflow-x: auto;
}
.tab-bar {
  display: flex;
  gap: 4px;
  min-width: max-content;
}
.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #7a90b0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  font-family: inherit;
}
.tab-btn:hover { background: #f4f7fb; color: #1a2b45; }
.tab-btn.active { background: #004AAD; color: #fff; }
.tab-icon { font-size: 12px; }

/* ── Stat grids ── */
.stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.stat-grid--3 { grid-template-columns: repeat(3, 1fr); }
.stat-grid--2 { grid-template-columns: repeat(2, 1fr); }

/* ── Stat cards ── */
.stat-card {
  display: flex; align-items: center; justify-content: space-between;
  border-radius: 12px; padding: 16px 18px; text-decoration: none;
  transition: transform 0.15s, box-shadow 0.15s; gap: 12px;
}
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); text-decoration: none; }
.stat-card--dark  { background: #051B35; border: 1px solid rgba(255,255,255,0.06); }
.stat-card--light { background: #fff; border: 1px solid #e4eaf2; }
.stat-info { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.stat-label { font-size: 12px; font-weight: 500; color: rgba(255,255,255,0.65); white-space: nowrap; }
.stat-value { font-size: 20px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stat-value--danger  { color: #e03535 !important; }
.stat-value--warning { color: #e08c00 !important; }
.stat-value--success { color: #16a34a !important; }
.text-success { color: #16a34a !important; }

.stat-icon { flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.stat-icon--blue, .stat-icon--green, .stat-icon--red, .stat-icon--orange,
.stat-icon--yellow, .stat-icon--purple, .stat-icon--teal {
  width: 42px; height: 42px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
}
.stat-icon--blue   { background: rgba(0,74,173,0.12);   color: #004AAD; }
.stat-icon--green  { background: rgba(22,163,74,0.12);  color: #16a34a; }
.stat-icon--red    { background: rgba(224,53,53,0.12);  color: #e03535; }
.stat-icon--orange { background: rgba(224,140,0,0.12);  color: #e08c00; }
.stat-icon--yellow { background: rgba(202,138,4,0.12);  color: #ca8a04; }
.stat-icon--purple { background: rgba(124,58,237,0.12); color: #7c3aed; }
.stat-icon--teal   { background: rgba(13,148,136,0.12); color: #0d9488; }
.stat-card--dark .stat-icon--green  { background: rgba(76,222,128,0.15);  color: #4cde80; }
.stat-card--dark .stat-icon--yellow { background: rgba(251,191,36,0.15);  color: #fbbf24; }
.stat-card--dark .stat-icon--red    { background: rgba(248,113,113,0.15); color: #f87171; }
.stat-card--dark .stat-icon--purple { background: rgba(196,181,253,0.15); color: #c4b5fd; }
.stat-card--dark .stat-icon--teal   { background: rgba(94,234,212,0.15);  color: #5eead4; }
.stat-card--dark .stat-icon--blue   { background: rgba(96,165,250,0.15);  color: #60a5fa; }

/* ── Charts ── */
.chart-card { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; padding: 20px; }
.chart-title { font-size: 14px; font-weight: 600; color: #1a2b45; margin-bottom: 16px; }

/* ── Table card ── */
.table-card { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; overflow: hidden; }
.table-card-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #e4eaf2; }
.table-card-title { margin: 0; font-size: 14px; font-weight: 600; color: #1a2b45; }
.period-select { border: 1px solid #dde5f0; border-radius: 6px; padding: 5px 10px; font-size: 13px; color: #004AAD; background: #f4f7fb; cursor: pointer; outline: none; }
.period-select:focus { border-color: #004AAD; }
.table-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; color: #7a90b0; }
.table thead tr { background: #f8fafc; }
.table thead th { font-size: 12px; font-weight: 600; color: #7a90b0; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e4eaf2; padding: 12px 16px; }
.table tbody td { padding: 13px 16px; vertical-align: middle; font-size: 13.5px; color: #1a2b45; border-bottom: 1px solid #f0f3f8; }
.table tbody tr:last-child td { border-bottom: none; }
.table-hover tbody tr:hover td { background: #f8fafc; }
.prod-img { width: 36px; height: 36px; border-radius: 8px; object-fit: cover; border: 1px solid #e4eaf2; }
.prod-name { font-weight: 600; font-size: 13.5px; }
.qty-badge { display: inline-block; background: #edf4ff; color: #004AAD; border-radius: 20px; padding: 2px 10px; font-size: 12.5px; font-weight: 600; }
.qty-low { background: #fff0f0; color: #e03535; }

/* ── Side Panels ── */
.side-panel { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; padding: 16px; }
.side-panel-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #7a90b0; margin: 0 0 14px; }

/* ── Quick Actions ── */
.quick-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.qa-btn { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 12px 8px; border-radius: 10px; background: #f8fafc; border: 1px solid #e4eaf2; text-decoration: none; color: #1a2b45; font-size: 12px; font-weight: 500; transition: background 0.15s, transform 0.15s, box-shadow 0.15s; text-align: center; }
.qa-btn:hover { background: #edf2fb; border-color: #c5d6f5; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,74,173,0.1); color: #004AAD; text-decoration: none; }
.qa-icon { width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 15px; }
.qa-icon--blue   { background: rgba(0,74,173,0.1);   color: #004AAD; }
.qa-icon--purple { background: rgba(124,58,237,0.1); color: #7c3aed; }
.qa-icon--teal   { background: rgba(13,148,136,0.1); color: #0d9488; }
.qa-icon--orange { background: rgba(224,140,0,0.1);  color: #e08c00; }
.qa-icon--green  { background: rgba(22,163,74,0.1);  color: #16a34a; }
.qa-icon--red    { background: rgba(224,53,53,0.1);  color: #e03535; }

/* ── Alert rows ── */
.alert-row { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f0f3f8; }
.alert-row:last-child { border-bottom: none; }
.alert-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.alert-dot--red    { background: #e03535; }
.alert-dot--orange { background: #e08c00; }
.alert-dot--blue   { background: #004AAD; }
.alert-dot--green  { background: #16a34a; }
.alert-dot--teal   { background: #0d9488; }
.alert-dot--purple { background: #7c3aed; }
.alert-text { flex: 1; display: flex; flex-direction: column; gap: 1px; }
.alert-label { font-size: 12px; color: #7a90b0; }
.alert-count { font-size: 15px; font-weight: 700; }
.alert-count--red    { color: #e03535; }
.alert-count--orange { color: #e08c00; }
.alert-count--blue   { color: #004AAD; }
.alert-count--green  { color: #16a34a; }
.alert-count--teal   { color: #0d9488; }
.alert-link { font-size: 11px; font-weight: 600; color: #004AAD; text-decoration: none; background: #edf2fb; border-radius: 4px; padding: 2px 8px; white-space: nowrap; transition: background 0.15s; }
.alert-link:hover { background: #d6e4fa; }

/* ── Module detail card ── */
.module-detail-card { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; padding: 20px; }
.module-detail-hd { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.mdh-icon { font-size: 22px; flex-shrink: 0; }
.mdh-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
.mdh-sub { font-size: 12px; color: #7a90b0; margin: 0; }
.module-stats-list { display: flex; flex-direction: column; gap: 12px; }
.msl-row { display: flex; align-items: center; gap: 10px; }
.msl-label { font-size: 12px; color: #7a90b0; min-width: 120px; flex-shrink: 0; }
.msl-bar-wrap { flex: 1; height: 8px; background: #f0f4f8; border-radius: 99px; overflow: hidden; }
.msl-bar { display: block; height: 100%; border-radius: 99px; min-width: 4px; transition: width 0.4s ease; }
.msl-bar--blue   { background: #004AAD; }
.msl-bar--green  { background: #16a34a; }
.msl-bar--red    { background: #e03535; }
.msl-bar--orange { background: #e08c00; }
.msl-val { font-size: 12px; font-weight: 700; min-width: 80px; text-align: right; white-space: nowrap; }
.msl-val--blue   { color: #004AAD; }
.msl-val--green  { color: #16a34a; }
.msl-val--red    { color: #e03535; }
.msl-val--orange { color: #e08c00; }
.module-detail-actions { display: flex; gap: 8px; margin-top: 16px; justify-content: flex-end; }
.mda-link { font-size: 12.5px; font-weight: 600; color: #004AAD; background: #edf2fb; border-radius: 7px; padding: 6px 14px; text-decoration: none; transition: background 0.15s; }
.mda-link:hover { background: #d6e4fa; text-decoration: none; }
.mda-link--primary { background: #004AAD; color: #fff; }
.mda-link--primary:hover { background: #003b8e; color: #fff; }

/* ── Empty tab state ── */
.empty-tab { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; background: #fff; border: 2px dashed #e4eaf2; border-radius: 12px; text-align: center; }
.empty-tab-icon { font-size: 40px; color: #c0c9d8; }
.empty-tab-title { font-size: 16px; font-weight: 700; color: #1a2b45; margin: 0; }
.empty-tab-sub { font-size: 13px; color: #7a90b0; margin: 0; max-width: 300px; line-height: 1.6; }
.btn-tab-action { display: inline-block; background: #004AAD; color: #fff; font-size: 13px; font-weight: 600; padding: 9px 20px; border-radius: 8px; text-decoration: none; transition: background 0.15s; }
.btn-tab-action:hover { background: #003b8e; text-decoration: none; color: #fff; }

/* ── Upgrade banner ── */
.upgrade-banner {
  display: flex; align-items: center; gap: 14px;
  background: linear-gradient(135deg, #fff7ed, #fef3c7);
  border: 1px solid #fcd34d; border-radius: 12px; padding: 16px 20px;
}
.upgrade-banner-icon { font-size: 22px; color: #d97706; flex-shrink: 0; }
.upgrade-banner-text { flex: 1; display: flex; flex-direction: column; gap: 3px; }
.upgrade-banner-text strong { font-size: 13.5px; color: #92400e; }
.upgrade-banner-text span { font-size: 12px; color: #b45309; line-height: 1.5; }
.upgrade-banner-btn {
  flex-shrink: 0; background: #d97706; color: #fff; font-size: 12.5px; font-weight: 700;
  padding: 8px 18px; border-radius: 8px; text-decoration: none; white-space: nowrap;
  transition: background 0.15s;
}
.upgrade-banner-btn:hover { background: #b45309; color: #fff; text-decoration: none; }

/* ── All Modules Grid ── */
.modules-section { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; padding: 20px; }
.modules-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #7a90b0; margin: 0 0 16px; }
.modules-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.mod-card {
  display: flex; align-items: center; gap: 12px;
  padding: 13px 14px; border-radius: 10px;
  border: 1px solid #e4eaf2; background: #fafbfd;
  text-decoration: none; color: #1a2b45;
  transition: border-color 0.15s, background 0.15s, transform 0.15s, box-shadow 0.15s;
}
.mod-card:hover {
  background: #fff; border-color: #c5d6f5;
  transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,74,173,0.09);
  text-decoration: none; color: #1a2b45;
}
.mod-icon {
  width: 38px; height: 38px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
}
.mod-info { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.mod-name { font-size: 13px; font-weight: 700; color: #1a2b45; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mod-desc { font-size: 11px; color: #9aabbd; line-height: 1.4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mod-arrow { font-size: 10px; color: #c5d0de; flex-shrink: 0; }
.mod-card:hover .mod-arrow { color: #004AAD; }

/* ── CRM tab helpers ── */
.crm-skeleton-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.crm-skeleton-chart { height:300px; background:#e8edf5; border-radius:12px; animation:crm-pulse 1.4s ease-in-out infinite; }
@keyframes crm-pulse { 0%,100%{opacity:1}50%{opacity:.5} }
.crm-chart-empty { height:300px; display:flex; align-items:center; justify-content:center; color:#b0bec8; font-size:13px; }
.qa-icon--yellow { background:rgba(202,138,4,0.12); color:#ca8a04; }

/* ── Responsive ── */
@media (max-width: 1199px) {
  .modules-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 991px) {
  .stat-grid    { grid-template-columns: repeat(2, 1fr); }
  .stat-grid--3 { grid-template-columns: repeat(2, 1fr); }
  .modules-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 576px) {
  .dashboard    { padding: 12px; gap: 14px; }
  .stat-grid    { grid-template-columns: 1fr 1fr; gap: 10px; }
  .stat-grid--3 { grid-template-columns: 1fr 1fr; }
  .stat-grid--2 { grid-template-columns: 1fr; }
  .stat-value   { font-size: 16px; }
  .dash-header  { flex-direction: column; align-items: flex-start; }
  .dash-wallet-id { align-items: flex-start; }
  .msl-label    { min-width: 90px; }
  .msl-val      { min-width: 60px; }
  .modules-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .mod-desc     { display: none; }
}
</style>
